import React from 'react';
import type { ReactNode } from 'react';
import {
  Platform,
  KeyboardAvoidingView,
  ScrollView,
  View,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

interface ScreenWrapperProps {
  /** Main screen content */
  children: ReactNode;
  /** Wrap children in a ScrollView. Default: true */
  scroll?: boolean;
  /** Use a KeyboardAvoidingView to move content above the keyboard. Default: true */
  keyboardAware?: boolean;
  /** Extra style for outer SafeAreaView */
  style?: StyleProp<ViewStyle>;
  /** Style for ScrollView / View inner container */
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** KeyboardAvoidingView behaviour for iOS. Default: 'padding' */
  iosKeyboardBehavior?: 'padding' | 'height' | 'position';
}

/**
 * Shared layout wrapper that takes care of:
 *  • Safe area padding (notches / status bar)
 *  • Optional KeyboardAvoidingView for input screens
 *  • Optional ScrollView so long content is scrollable on small phones
 *
 * Usage:
 * ```tsx
 * <ScreenWrapper>
 *   ...screen JSX...
 * </ScreenWrapper>
 * ```
 */
const ScreenWrapper: React.FC<ScreenWrapperProps> = ({
  children,
  scroll = true,
  keyboardAware = true,
  style,
  contentContainerStyle,
  iosKeyboardBehavior = 'padding',
}) => {
  const Container = scroll ? ScrollView : View;

  const Inner = (
    <Container
      style={[styles.flex, contentContainerStyle]}
      contentContainerStyle={scroll ? [styles.flexGrow, contentContainerStyle] : undefined}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      {children}
    </Container>
  );

  // Optionally wrap in KeyboardAvoidingView
  const maybeKeyboardAware = keyboardAware ? (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? iosKeyboardBehavior : undefined}
      keyboardVerticalOffset={Platform.select({ ios: 0, android: 0 })}
    >
      {Inner}
    </KeyboardAvoidingView>
  ) : (
    Inner
  );

  return (
    <SafeAreaView style={[styles.flex, style]} edges={['left', 'right', 'bottom', 'top']}>
      {maybeKeyboardAware}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  flex: { flex: 1 },
  flexGrow: { flexGrow: 1 },
});

export default ScreenWrapper;
